import { createRoute, Link } from "@tanstack/solid-router";
import { createSignal, Show } from "solid-js";
import { authRoute } from "./route";
import { auth } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@/components/ui/text-field";
import { Logo } from "@/components/ui/logo";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { msg } from "@lingui/core/macro";
import { GlassSurface } from "@/components/ui/glass-surface";
import { AnimatedSection } from "@/components/ui/animated-section";

function ForgotPasswordPage() {
  const { t } = useI18n();
  const [email, setEmail] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(false);
  const [sent, setSent] = createSignal(false);

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await auth.forgotPassword({
        email: email(),
      });
      setSent(true);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t(msg`Unexpected error. Please try again.`));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <GlassSurface borderEdge="accent">
      <CardHeader class="flex flex-col items-center space-y-4 pt-10 pb-6">
        <Link to="/" class="transition-transform hover:scale-105 active:scale-95">
          <Logo size="lg" />
        </Link>
        <CardTitle class="text-2xl">{t(msg`Reset Password`)}</CardTitle>
        <CardDescription class="text-center">
          {t(msg`Enter your email and we'll send you a link to reset your password`)}
        </CardDescription>
      </CardHeader>

      <Show
        when={!sent()}
        fallback={
          <CardContent class="space-y-4">
            <div class="rounded-lg border border-border bg-muted/40 p-4 text-sm text-center space-y-1">
              <p class="font-medium">{t(msg`Email sent`)}</p>
              <p class="text-muted-foreground">
                {t(msg`Check your inbox at`)} <span class="font-medium">{email()}</span>{" "}
                {t(msg`and follow the link to reset your password.`)}
              </p>
            </div>
            <CardFooter class="flex flex-col gap-3 px-0 pb-0">
              <Link to="/login" class="w-full">
                <Button variant="outline" class="w-full">
                  {t(msg`Back to sign in`)}
                </Button>
              </Link>
            </CardFooter>
          </CardContent>
        }
      >
        <form onSubmit={(e) => void handleSubmit(e)}>
          <CardContent class="space-y-4">
            <div class="space-y-2">
              <Label for="email">{t(msg`Email`)}</Label>
              <TextField
                id="email"
                type="email"
                placeholder={t(msg`your@email.com`)}
                autocomplete="email"
                required
                value={email()}
                onInput={(e) => setEmail(e.currentTarget.value)}
              />
            </div>
            <AnimatedSection show={Boolean(error())}>
              <p class="text-sm text-destructive">{error()}</p>
            </AnimatedSection>
          </CardContent>

          <CardFooter class="flex flex-col gap-3">
            <Button type="submit" class="w-full" disabled={loading()}>
              {loading() ? t(msg`Sending…`) : t(msg`Send link`)}
            </Button>
            <p class="text-center text-sm text-muted-foreground">
              <Link to="/login" class="text-primary underline-offset-4 hover:underline font-medium">
                {t(msg`Back to sign in`)}
              </Link>
            </p>
            <p class="text-center text-xs text-muted-foreground/60 mt-2">
              <Link to="/" class="hover:text-primary transition-colors">
                {t(msg`← Back to home`)}
              </Link>
            </p>
          </CardFooter>
        </form>
      </Show>
    </GlassSurface>
  );
}

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});
